import { Directive, ElementRef, HostListener, Input, inject } from '@angular/core';
import { arrowNavigationHandler } from './arrow-navigation.handler';

/**
 * Directive for enabling keyboard navigation using arrow keys.
 * 
 * @property {string} arrowNavigationMode - Determines the navigation pattern:
 *   - "two": Enables navigation with UP and DOWN arrow keys.
 *   - "four": Adds LEFT and RIGHT arrow keys for additional navigation options, such as triggering click events.
 * 
 * @property {string} arrowNavigationChildren - CSS selector for identifying focusable elements within the directive's scope. 
 *   Default: "[tabindex]:not([tabindex='-1'])", selecting elements with a `tabindex` attribute not equal to '-1'.
 *   Can be customized, e.g., "li" to select list items.
 * 
 * @example
 * <ul rxArrowNavigation arrowNavigationMode="two" arrowNavigationChildren="li">
 *   <li (click)="doSomething($event)" (keydown.enter)="doSomething($event)">Item 1</li>
 *   <li (click)="doSomething($event)" (keydown.enter)="doSomething($event)">Item 2</li>
 * </ul>
 */
@Directive({
  standalone: true,
  selector: '[rxArrowNavigation]',
})
export class ArrowNavigationDirective {
  @Input() arrowNavigationMode: 'two' | 'four' = 'two';
  @Input() arrowNavigationChildren = "[tabindex]:not([tabindex='-1'])"; // all focusable elements

  readonly elementRef = inject(ElementRef);
  @HostListener('document:keydown', ['$event']) onKeydown(event: KeyboardEvent): void {
    arrowNavigationHandler(this.elementRef, this.arrowNavigationChildren, event, this.arrowNavigationMode);
  }
}
